// src/routes/raci/index.tsx
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { raciStore, raciActions } from '../../stores/raciStore';
import { riskStore } from '../../stores/riskStore';
import { nonInfoSecRiskStore } from '../../stores/nonInfoSecRiskStore';
import { soaStore } from '../../stores/soaStore';
import { NavigationSidebar } from '../../components/layout/NavigationSidebar';
import { ModuleHeader } from '../../components/layout/ModuleHeader';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/Button';
import { useToast } from '../../components/ui/Toast';
import { usePermissions } from '../../hooks/usePermissions';
import { iso27001Controls, controlThemes } from '../../data/iso27001Controls';
import { downloadBlob } from '../../utils/exportUtils';
import { formatDate, formatToday } from '../../utils/dateUtils';
import { api } from '../../utils/api';
import { DocumentVersionModal } from '../../components/shared/DocumentVersionModal';
import { RaciRow } from '../../components/raci/RaciRow';
import type { RaciRowItem, RaciAssignment, RaciMode } from '../../types/raci';

export const Route = createFileRoute('/raci/')({
  component: RACIModule,
});

function RACIModule() {
  const raciSnap = useSnapshot(raciStore);
  const riskSnap = useSnapshot(riskStore);
  const nonInfoSecSnap = useSnapshot(nonInfoSecRiskStore);
  const soaSnap = useSnapshot(soaStore);
  const { canEdit } = usePermissions();
  const { showToast, ToastContainer } = useToast();
  const [showVersionModal, setShowVersionModal] = useState(false);

  // Build row items based on current mode
  const rowItems = useMemo(() => {
    if (raciSnap.mode === 'soa') {
      return getSoaRows();
    }
    return getTreatmentRows();
  }, [raciSnap.mode, riskSnap.risks, nonInfoSecSnap.risks, soaSnap.justifications]);

  function getSoaRows(): RaciRowItem[] {
    // Standard ISO controls (exclude the placeholder "Custom control" entry)
    const standardRows: RaciRowItem[] = iso27001Controls
      .filter((c) => c.theme !== 'Custom')
      .map((c) => {
        const linkedRisks = (riskSnap.risks || []).filter((r) =>
          r.controls?.some(ctrl => ctrl.control === c.control) || r.control === c.control
        );
        const hasLinkedRisks = linkedRisks.length > 0;
        // Collect unique non-empty risk owners from linked risks
        const owners = [...new Set(linkedRisks.map((r) => r.riskOwner).filter(Boolean))];
        return {
          key: c.control,
          label: c.control,
          group: c.theme,
          sublabel: c.objective,
          used: hasLinkedRisks,
          riskOwners: owners.length > 0 ? owners : undefined,
        };
      });

    // Custom controls — one row per unique customControl text from risks
    const customTexts = new Set<string>();
    const customOwners: Record<string, string[]> = {};
    const customDescriptions: Record<string, string> = {};
    for (const risk of riskSnap.risks || []) {
      // Check controls array for custom controls
      const customEntries = (risk.controls || []).filter(c => c.control === 'Custom control' && c.customControl);
      // Fallback to flat field
      if (customEntries.length === 0 && risk.control === 'Custom control' && risk.customControl) {
        customEntries.push({ control: 'Custom control', customControl: risk.customControl, controlImplementation: risk.controlImplementation || '' });
      }
      for (const entry of customEntries) {
        const name = entry.customControl!;
        customTexts.add(name);
        if (risk.riskOwner) {
          if (!customOwners[name]) customOwners[name] = [];
          if (!customOwners[name].includes(risk.riskOwner)) {
            customOwners[name].push(risk.riskOwner);
          }
        }
        if (entry.controlImplementation && !customDescriptions[name]) {
          customDescriptions[name] = entry.controlImplementation;
        }
      }
    }

    const customRows: RaciRowItem[] = Array.from(customTexts).map((text) => ({
      key: `Custom: ${text}`,
      label: text,
      group: 'Custom',
      sublabel: customDescriptions[text] || 'Organisation-specific custom control',
      used: true,
      riskOwners: customOwners[text]?.length > 0 ? customOwners[text] : undefined,
    }));

    return [...standardRows, ...customRows];
  }

  function getTreatmentRows(): RaciRowItem[] {
    const items: RaciRowItem[] = [];

    // InfoSec risks with active treatments
    for (const risk of riskSnap.risks) {
      if (risk.treatment && risk.treatment !== 'Accept the risk' && risk.treatment !== '') {
        // Build a meaningful label: use threat as fallback since InfoSec risks don't have a title field
        const riskLabel = risk.riskTitle || risk.threat || 'Untitled Risk';
        const sublabelParts = [risk.treatment];
        if (risk.treatmentDetail) sublabelParts[0] += ': ' + risk.treatmentDetail;
        // Show control info from controls array
        const controlNames = (risk.controls || [])
          .map(c => c.control === 'Custom control' ? (c.customControl || 'Custom') : c.control)
          .filter(Boolean);
        // Fallback to flat field
        if (controlNames.length === 0 && risk.control) {
          controlNames.push(risk.control === 'Custom control' ? (risk.customControl || 'Custom') : risk.control);
        }
        if (controlNames.length > 0) sublabelParts.push(controlNames.join('; '));
        if (risk.riskDescription) sublabelParts.push(risk.riskDescription);
        items.push({
          key: risk.referenceNumber,
          label: `${risk.referenceNumber} — ${riskLabel}`,
          group: 'InfoSec Risk Treatments',
          sublabel: sublabelParts.join(' — '),
          source: 'infosec',
          riskOwner: risk.riskOwner || undefined,
        });
      }
    }

    // Business risks with active treatments
    for (const risk of nonInfoSecSnap.risks) {
      if (risk.treatment && risk.treatment !== 'Accept the risk' && risk.treatment !== '') {
        const sublabelParts = [risk.treatment];
        if (risk.treatmentDetail) sublabelParts[0] += ': ' + risk.treatmentDetail;
        if (risk.riskDescription) sublabelParts.push(risk.riskDescription);
        items.push({
          key: risk.referenceNumber,
          label: `${risk.referenceNumber} — ${risk.riskTitle || 'Untitled Risk'}`,
          group: 'Business Risk Treatments',
          sublabel: sublabelParts.join(' — '),
          source: 'business',
          riskOwner: risk.riskOwner || undefined,
        });
      }
    }

    return items;
  }

  // Auto-populate RACI roles from risk owners (only fills empty slots, never overwrites)
  const autoPopulatedRef = useRef<string>('');
  useEffect(() => {
    if (raciSnap.loading || rowItems.length === 0) return;
    // Build a fingerprint so we only auto-populate once per mode+assignments load
    const fingerprint = `${raciSnap.mode}:${Object.keys(raciSnap.assignments).length}:${rowItems.length}`;
    if (autoPopulatedRef.current === fingerprint) return;
    autoPopulatedRef.current = fingerprint;

    for (const row of rowItems) {
      const existing = raciSnap.assignments[row.key];

      if (raciSnap.mode === 'treatment' && row.riskOwner) {
        // Treatment mode: risk owner → Accountable (only if no accountable set)
        const hasAccountable = existing?.accountable !== null && existing?.accountable !== undefined;
        if (!hasAccountable) {
          raciActions.setAccountable(row.key, { userId: '', name: row.riskOwner, freeText: true });
        }
      } else if (raciSnap.mode === 'soa' && row.riskOwners && row.riskOwners.length > 0) {
        // SoA mode: risk owners → Responsible (only if no responsible set)
        const hasResponsible = existing?.responsible && existing.responsible.length > 0;
        if (!hasResponsible) {
          const people = row.riskOwners.map((name) => ({ userId: '', name, freeText: true }));
          raciActions.setAssignment(row.key, 'responsible', people);
        }
      }
    }
  }, [raciSnap.loading, raciSnap.mode, rowItems, raciSnap.assignments]);

  // Available groups for the filter dropdown
  const availableGroups = useMemo(() => {
    if (raciSnap.mode === 'soa') {
      return controlThemes;
    }
    const groups = new Set(rowItems.map((r) => r.group));
    return Array.from(groups);
  }, [raciSnap.mode, rowItems]);

  // Filtered rows
  const filteredRows = useMemo(() => {
    let rows = rowItems;

    // Group filter
    if (raciSnap.filterGroup !== 'all') {
      rows = rows.filter((r) => r.group === raciSnap.filterGroup);
    }

    // Search filter
    if (raciSnap.searchTerm) {
      const search = raciSnap.searchTerm.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.label.toLowerCase().includes(search) ||
          (r.sublabel && r.sublabel.toLowerCase().includes(search)),
      );
    }

    // Assignment filter
    if (raciSnap.filterAssigned !== 'all') {
      rows = rows.filter((r) => {
        const a = raciSnap.assignments[r.key];
        const hasAssignment =
          a &&
          (a.responsible.length > 0 ||
            a.accountable !== null ||
            a.consulted.length > 0 ||
            a.informed.length > 0);
        return raciSnap.filterAssigned === 'assigned' ? hasAssignment : !hasAssignment;
      });
    }

    // Usage filter (SoA mode only)
    if (raciSnap.mode === 'soa' && raciSnap.filterUsage !== 'all') {
      rows = rows.filter((r) =>
        raciSnap.filterUsage === 'used' ? r.used : !r.used,
      );
    }

    return rows;
  }, [rowItems, raciSnap.filterGroup, raciSnap.searchTerm, raciSnap.filterAssigned, raciSnap.filterUsage, raciSnap.mode, raciSnap.assignments]);

  // Group filtered rows by their group field
  const groupedRows = useMemo(() => {
    const groups: Record<string, RaciRowItem[]> = {};
    for (const row of filteredRows) {
      if (!groups[row.group]) groups[row.group] = [];
      groups[row.group].push(row);
    }
    return groups;
  }, [filteredRows]);

  // Statistics
  const stats = useMemo(() => {
    let total = rowItems.length;
    let fullyAssigned = 0;
    let partiallyAssigned = 0;
    let unassigned = 0;
    let usedCount = 0;
    let unusedCount = 0;

    for (const row of rowItems) {
      // Track used/unused for SoA mode
      if (row.used === true) usedCount++;
      else if (row.used === false) unusedCount++;

      const a = raciSnap.assignments[row.key];
      if (!a) {
        unassigned++;
        continue;
      }
      const hasR = a.responsible.length > 0;
      const hasA = a.accountable !== null;
      const hasC = a.consulted.length > 0;
      const hasI = a.informed.length > 0;

      if (hasR && hasA && hasC && hasI) {
        fullyAssigned++;
      } else if (hasR || hasA || hasC || hasI) {
        partiallyAssigned++;
      } else {
        unassigned++;
      }
    }

    const completionPercent = total > 0 ? Math.round((fullyAssigned / total) * 100) : 0;

    return { total, fullyAssigned, partiallyAssigned, unassigned, completionPercent, usedCount, unusedCount };
  }, [rowItems, raciSnap.assignments]);

  // Build all SoA rows (for export regardless of current mode)
  const allSoaRows = useMemo(() => getSoaRows(), [riskSnap.risks]);
  // Build all treatment rows (for export regardless of current mode)
  const allTreatmentRows = useMemo(() => getTreatmentRows(), [riskSnap.risks, nonInfoSecSnap.risks]);
  // Whether both modes have data (for "Export Both" visibility)
  const hasBothModes = allSoaRows.length > 0 && allTreatmentRows.length > 0;

  // Parameterised CSV export
  function exportCSV(rows: RaciRowItem[], isSoa: boolean, assignments: Record<string, any>) {
    const escCSV = (val: string) => `"${(val || '').replace(/"/g, '""')}"`;
    const today = formatToday();
    const modeLabel = isSoa ? 'SoA Controls' : 'Risk Treatments';

    const metaRows: string[] = [
      escCSV(`RACI Matrix — ${modeLabel}`),
      `${escCSV('Company')},${escCSV(soaSnap.companyInfo.companyName)}`,
      `${escCSV('Issue No')},${escCSV(soaSnap.companyInfo.issueNo)},${escCSV('Date')},${escCSV(soaSnap.companyInfo.date || today)}`,
      `${escCSV('Prepared By')},${escCSV(soaSnap.companyInfo.preparedBy || '')},${escCSV('Reviewed By')},${escCSV(soaSnap.companyInfo.reviewedBy || '')}`,
      `${escCSV('Approved By')},${escCSV(soaSnap.companyInfo.approvedBy || '')},${escCSV('Approved Date')},${escCSV(soaSnap.companyInfo.approvedDate ? formatDate(soaSnap.companyInfo.approvedDate) : '')}`,
      `${escCSV('Next Review')},${escCSV(soaSnap.companyInfo.nextReviewDate ? formatDate(soaSnap.companyInfo.nextReviewDate) : '')}`,
      '',
    ];

    const headers = isSoa
      ? ['Item', 'Group', 'Status', 'Responsible', 'Accountable', 'Consulted', 'Informed']
      : ['Item', 'Group', 'Responsible', 'Accountable', 'Consulted', 'Informed'];

    const dataRows = rows.map((row) => {
      const a = assignments[row.key];
      const baseCols = [
        row.label,
        row.group,
        ...(isSoa ? [row.used ? 'Used' : 'Not Used'] : []),
        a?.responsible?.map((p: any) => p.name).join('; ') || '',
        a?.accountable?.name || '',
        a?.consulted?.map((p: any) => p.name).join('; ') || '',
        a?.informed?.map((p: any) => p.name).join('; ') || '',
      ];
      return baseCols.map(escCSV).join(',');
    });

    const csv = [...metaRows, headers.join(','), ...dataRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const modeLabelFile = isSoa ? 'soa-controls' : 'risk-treatments';
    downloadBlob(blob, `raci-matrix-${modeLabelFile}-${new Date().toISOString().split('T')[0]}.csv`);
  }

  // Parameterised PDF export
  function exportPDF(rows: RaciRowItem[], isSoa: boolean, assignments: Record<string, any>, modeKey: string) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const today = formatToday();
    const modeLabel = isSoa ? 'SoA Controls' : 'Risk Treatments';

    if (soaSnap.companyInfo.logo) {
      try {
        const img = new Image();
        img.src = soaSnap.companyInfo.logo;
        const logoHeight = 20;
        const logoWidth = img.naturalWidth && img.naturalHeight
          ? (img.naturalWidth / img.naturalHeight) * logoHeight
          : logoHeight;
        const format = soaSnap.companyInfo.logo.includes('image/png') ? 'PNG' : 'JPEG';
        doc.addImage(soaSnap.companyInfo.logo, format, 14, 6, logoWidth, logoHeight);
      } catch {
        // Continue without logo
      }
    }

    doc.setFontSize(20);
    doc.setTextColor(79, 70, 229);
    doc.text(`RACI Matrix — ${modeLabel}`, pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(14);
    doc.setTextColor(100, 100, 100);
    doc.text(soaSnap.companyInfo.companyName, pageWidth / 2, 22, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text('Issue No: ' + soaSnap.companyInfo.issueNo, 14, 30);
    doc.text('Date: ' + (soaSnap.companyInfo.date || today), pageWidth - 14, 30, { align: 'right' });

    let signatoryY = 35;
    const sigParts: string[] = [];
    if (soaSnap.companyInfo.preparedBy) sigParts.push('Prepared By: ' + soaSnap.companyInfo.preparedBy);
    if (soaSnap.companyInfo.reviewedBy) sigParts.push('Reviewed By: ' + soaSnap.companyInfo.reviewedBy);
    if (soaSnap.companyInfo.approvedBy) {
      let approvedText = 'Approved By: ' + soaSnap.companyInfo.approvedBy;
      if (soaSnap.companyInfo.approvedDate) approvedText += ' (' + formatDate(soaSnap.companyInfo.approvedDate) + ')';
      sigParts.push(approvedText);
    }
    if (sigParts.length > 0) {
      doc.setFontSize(8);
      doc.text(sigParts.join('  |  '), 14, signatoryY);
      signatoryY += 5;
    }
    if (soaSnap.companyInfo.nextReviewDate) {
      doc.setFontSize(8);
      doc.text('Next Review: ' + formatDate(soaSnap.companyInfo.nextReviewDate), 14, signatoryY);
      signatoryY += 5;
    }

    // Compute quick stats for the passed rows
    let totalExport = rows.length;
    let fullyExport = 0;
    let partialExport = 0;
    let unassignedExport = 0;
    let usedExport = 0;
    let unusedExport = 0;
    for (const row of rows) {
      if (row.used === true) usedExport++;
      else if (row.used === false) unusedExport++;
      const a = assignments[row.key];
      if (!a) { unassignedExport++; continue; }
      const hasR = a.responsible?.length > 0;
      const hasA = a.accountable !== null && a.accountable !== undefined;
      const hasC = a.consulted?.length > 0;
      const hasI = a.informed?.length > 0;
      if (hasR && hasA && hasC && hasI) fullyExport++;
      else if (hasR || hasA || hasC || hasI) partialExport++;
      else unassignedExport++;
    }
    const completionExport = totalExport > 0 ? Math.round((fullyExport / totalExport) * 100) : 0;

    doc.setFontSize(9);
    const statsY = signatoryY + 3;
    const statsSpacing = 50;
    doc.setTextColor(0, 0, 0);
    doc.text('Total: ' + totalExport, 14, statsY);
    if (isSoa) {
      doc.text('Used: ' + usedExport, 14 + statsSpacing, statsY);
      doc.text('Not Used: ' + unusedExport, 14 + statsSpacing * 2, statsY);
      doc.text('Fully Assigned: ' + fullyExport, 14 + statsSpacing * 3, statsY);
      doc.text('Completion: ' + completionExport + '%', 14 + statsSpacing * 4, statsY);
    } else {
      doc.text('Fully Assigned: ' + fullyExport, 14 + statsSpacing, statsY);
      doc.text('Partially: ' + partialExport, 14 + statsSpacing * 2, statsY);
      doc.text('Unassigned: ' + unassignedExport, 14 + statsSpacing * 3, statsY);
      doc.text('Completion: ' + completionExport + '%', 14 + statsSpacing * 4, statsY);
    }

    const tableHead = isSoa
      ? [['Item', 'Group', 'Status', 'Responsible', 'Accountable', 'Consulted', 'Informed']]
      : [['Item', 'Group', 'Responsible', 'Accountable', 'Consulted', 'Informed']];

    const tableData = rows.map((row) => {
      const a = assignments[row.key];
      return [
        row.label,
        row.group,
        ...(isSoa ? [row.used ? 'Used' : 'Not Used'] : []),
        a?.responsible?.map((p: any) => p.name).join(', ') || '-',
        a?.accountable?.name || '-',
        a?.consulted?.map((p: any) => p.name).join(', ') || '-',
        a?.informed?.map((p: any) => p.name).join(', ') || '-',
      ];
    });

    const columnStyles: { [key: string]: Partial<{ cellWidth: number }> } = isSoa
      ? {
          0: { cellWidth: 60 },
          1: { cellWidth: 30 },
          2: { cellWidth: 18 },
          3: { cellWidth: 38 },
          4: { cellWidth: 38 },
          5: { cellWidth: 38 },
          6: { cellWidth: 38 },
        }
      : {
          0: { cellWidth: 70 },
          1: { cellWidth: 35 },
          2: { cellWidth: 40 },
          3: { cellWidth: 40 },
          4: { cellWidth: 40 },
          5: { cellWidth: 40 },
        };

    autoTable(doc, {
      head: tableHead,
      body: tableData,
      startY: statsY + 6,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [79, 70, 229], textColor: 255, fontStyle: 'bold' },
      columnStyles,
      alternateRowStyles: { fillColor: [249, 250, 251] },
      didParseCell: isSoa
        ? (data: any) => {
            if (data.section === 'body') {
              const statusColIndex = 2;
              const rowData = data.row.raw as string[];
              if (rowData && rowData[statusColIndex] === 'Not Used') {
                data.cell.styles.textColor = [160, 160, 160];
                if (data.column.index === statusColIndex) {
                  data.cell.styles.fontStyle = 'italic';
                }
              }
            }
          }
        : undefined,
      didDrawPage: () => {
        const footerY = doc.internal.pageSize.getHeight() - 10;
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(
          'Created using Certain\u00AE \u2014 Asset Risk Manager',
          14,
          footerY,
          { align: 'left' },
        );
        doc.text(
          'Generated on ' + today + ' | RACI Matrix',
          pageWidth / 2,
          footerY,
          { align: 'center' },
        );
        doc.text(
          'Page ' + doc.internal.pages.length,
          pageWidth - 14,
          footerY,
          { align: 'right' },
        );
      },
    });

    const filename = `raci-matrix-${modeKey}-${soaSnap.companyInfo.issueNo.replace(/\./g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  }

  // Current mode export wrappers
  function handleExportCSV() {
    exportCSV(filteredRows, raciSnap.mode === 'soa', raciSnap.assignments);
    showToast('CSV exported successfully', 'success');
  }

  function handleExportPDF() {
    exportPDF(filteredRows, raciSnap.mode === 'soa', raciSnap.assignments, raciSnap.mode);
    showToast('PDF exported successfully', 'success');
  }

  // Fetch assignments for a specific mode (independent of current store mode)
  async function fetchAssignmentsForMode(mode: RaciMode): Promise<Record<string, RaciAssignment>> {
    try {
      const data = (await api.get(`/raci?mode=${mode}`)) as Record<string, RaciAssignment>;
      return data || {};
    } catch {
      return {};
    }
  }

  // Export both modes — fetches the other mode's assignments on demand
  async function handleExportBothCSV() {
    const currentMode = raciSnap.mode;
    const otherMode: RaciMode = currentMode === 'soa' ? 'treatment' : 'soa';
    const otherAssignments = await fetchAssignmentsForMode(otherMode);

    const soaAssignments = currentMode === 'soa' ? raciSnap.assignments : otherAssignments;
    const treatmentAssignments = currentMode === 'treatment' ? raciSnap.assignments : otherAssignments;

    exportCSV(allSoaRows, true, soaAssignments);
    setTimeout(() => {
      exportCSV(allTreatmentRows, false, treatmentAssignments);
    }, 500);
    showToast('Both CSV files exported', 'success');
  }

  async function handleExportBothPDF() {
    const currentMode = raciSnap.mode;
    const otherMode: RaciMode = currentMode === 'soa' ? 'treatment' : 'soa';
    const otherAssignments = await fetchAssignmentsForMode(otherMode);

    const soaAssignments = currentMode === 'soa' ? raciSnap.assignments : otherAssignments;
    const treatmentAssignments = currentMode === 'treatment' ? raciSnap.assignments : otherAssignments;

    exportPDF(allSoaRows, true, soaAssignments, 'soa');
    setTimeout(() => {
      exportPDF(allTreatmentRows, false, treatmentAssignments, 'treatment');
    }, 500);
    showToast('Both PDF files exported', 'success');
  }

  // Print
  function handlePrint() {
    window.print();
  }

  return (
    <div className="flex flex-1 overflow-hidden bg-gray-50">
      <NavigationSidebar />
      <div className="flex-1 overflow-auto min-w-0">
        <ModuleHeader
          title="RACI Matrix"
          subtitle="Assign Responsible, Accountable, Consulted and Informed roles"
        />

        <div className="p-6">
          {/* Mode Toggle */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <ModeButton
                active={raciSnap.mode === 'soa'}
                onClick={() => raciActions.setMode('soa')}
                label="SoA Controls"
              />
              <ModeButton
                active={raciSnap.mode === 'treatment'}
                onClick={() => raciActions.setMode('treatment')}
                label="Risk Treatments"
              />
            </div>
            <div className="flex items-center gap-2 print:hidden flex-wrap">
              <Button size="sm" variant="secondary" onClick={() => setShowVersionModal(true)}>
                <Icon name="FileText" className="w-4 h-4 mr-1" />
                Document Version
              </Button>
              <Button size="sm" variant="secondary" onClick={handleExportCSV}>
                <Icon name="Download" className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Button size="sm" variant="secondary" onClick={handleExportPDF}>
                <Icon name="FileText" className="w-4 h-4 mr-1" />
                PDF
              </Button>
              <Button size="sm" variant="secondary" onClick={handlePrint}>
                <Icon name="Printer" className="w-4 h-4 mr-1" />
                Print
              </Button>
              {hasBothModes && (
                <>
                  <span className="text-gray-300 mx-1">|</span>
                  <Button size="sm" variant="secondary" onClick={handleExportBothCSV}>
                    <Icon name="Download" className="w-4 h-4 mr-1" />
                    Both CSV
                  </Button>
                  <Button size="sm" variant="secondary" onClick={handleExportBothPDF}>
                    <Icon name="FileText" className="w-4 h-4 mr-1" />
                    Both PDF
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Statistics */}
          <div className={`grid grid-cols-2 gap-4 mb-6 print:hidden ${raciSnap.mode === 'soa' ? 'md:grid-cols-7' : 'md:grid-cols-5'}`}>
            <StatCard label="Total Items" value={stats.total} color="text-gray-800" delay={0} />
            {raciSnap.mode === 'soa' && (
              <>
                <StatCard label="Used Controls" value={stats.usedCount} color="text-teal-600" delay={0.05} />
                <StatCard label="Not Used" value={stats.unusedCount} color="text-gray-400" delay={0.1} />
              </>
            )}
            <StatCard label="Fully Assigned" value={stats.fullyAssigned} color="text-green-600" delay={0.15} />
            <StatCard label="Partially Assigned" value={stats.partiallyAssigned} color="text-amber-600" delay={0.2} />
            <StatCard label="Unassigned" value={stats.unassigned} color="text-gray-400" delay={0.25} />
            <StatCard label="Completion" value={`${stats.completionPercent}%`} color="text-purple-600" delay={0.3} />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-6 flex-wrap print:hidden">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder={raciSnap.mode === 'soa' ? 'Search controls...' : 'Search treatments...'}
                value={raciSnap.searchTerm}
                onChange={(e) => raciActions.setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
              />
            </div>

            {/* Group filter */}
            <select
              value={raciSnap.filterGroup}
              onChange={(e) => raciActions.setFilterGroup(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white"
            >
              <option value="all">All Groups</option>
              {availableGroups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>

            {/* Assignment filter */}
            <select
              value={raciSnap.filterAssigned}
              onChange={(e) => raciActions.setFilterAssigned(e.target.value as 'all' | 'assigned' | 'unassigned')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white"
            >
              <option value="all">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
            </select>

            {/* Usage filter (SoA mode only) */}
            {raciSnap.mode === 'soa' && (
              <select
                value={raciSnap.filterUsage}
                onChange={(e) => raciActions.setFilterUsage(e.target.value as 'all' | 'used' | 'unused')}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none bg-white"
              >
                <option value="all">All Controls</option>
                <option value="used">Used Controls</option>
                <option value="unused">Unused Controls</option>
              </select>
            )}
          </div>

          {/* Loading state */}
          {raciSnap.loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
              <p className="text-gray-500 mt-4">Loading RACI assignments...</p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center py-16">
              <svg
                className="w-16 h-16 text-gray-300 mx-auto mb-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <h3 className="text-lg font-semibold text-gray-600 mb-1">No Items Found</h3>
              <p className="text-gray-400 text-sm">
                {raciSnap.mode === 'treatment'
                  ? 'No risks with active treatments found. Add treatments to your risks first.'
                  : 'No controls match your current filters.'}
              </p>
            </div>
          ) : (
            /* Grouped rows */
            <div className="space-y-8">
              {Object.entries(groupedRows).map(([group, rows]) => (
                <div key={group}>
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-lg font-bold text-gray-800">{group}</h3>
                    <span className="text-sm text-gray-400">
                      {rows.length} {rows.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {rows.map((row) => (
                      <RaciRow
                        key={row.key}
                        item={row}
                        assignment={raciSnap.assignments[row.key] as any}
                        users={raciSnap.users as any}
                        canEdit={canEdit}
                        dimmed={row.used === false}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Document Version Modal (shared with SoA) */}
      <DocumentVersionModal
        isOpen={showVersionModal}
        onClose={() => setShowVersionModal(false)}
        onSave={() => showToast('Document version saved', 'success')}
      />
      <ToastContainer />
    </div>
  );
}

// ─── Subcomponents ───

function ModeButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
        active
          ? 'bg-white shadow-sm text-purple-700'
          : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

function StatCard({
  label,
  value,
  color,
  delay,
}: {
  label: string;
  value: number | string;
  color: string;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white rounded-xl shadow-md p-6"
    >
      <p className="text-gray-500 text-sm font-medium">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
    </motion.div>
  );
}
